<template>
        <modal
            name="calculate-mortgage"
            ref="calculate-mortgage"
            extra-class="full-screen-modal calculate-mortgage-modal"
            :title="lang.labels.calculate_mortgage"
        >
            <template slot="body">
                <img
                    v-if="lang.mortgage.logo"
                    :src="lang.mortgage.logo"
                    :alt="lang.mortgage.logo_title"
                    class="mortgage-logo"
                />

                <div class="calculate-mortgage-container">
                    <div
                        v-if="options.rate_visible"
                    >
                        {{ lang.labels.mortgage_rate }}: {{ this.durations[!this.options.fixed_rate ? this.local_duration : 0].TASSO__FIN }} %
                    </div>
                    <div class="form">

                        <my-multiselect
                            v-if="options.user"
                            v-model="user"
                            :options="formattedUser"
                            :allow-empty="false"
                            :placeholder="trans('labels.user')"
                            :name="trans('labels.user')"
                            label="name"
                            track-by="id"
                            @input="changeUser"
                        />

                        <my-input
                            v-model="formatted_price"
                            name="price"
                            type="text"
                            disabled
                            :label="lang.labels.estate_price"
                        >
                            <template v-slot:append>
                                {{ lang.labels.currency }}
                            </template>
                        </my-input>

                        <my-input
                            v-model="local_mortgage"
                            @input="changeMortgage"
                            name="mortgage"
                            type="text"
                            :disabled="options.user ? true : false"
                            :label="lang.labels.estate_mortgage"
                        >
                            <template v-slot:append>
                                {{ lang.labels.currency }}
                            </template>
                        </my-input>

                        <my-multiselect
                            v-model="duration_object"
                            :options="formattedDurations"
                            :allow-empty="false"
                            :placeholder="trans('labels.mortgage_duration')"
                            :name="trans('labels.mortgage_duration')"
                            label="name"
                            track-by="id"
                            @input="local_duration = duration_object"
                        />
                    </div>

                    <div class="chart">
                        <div class="chart-upper-legend">
                            {{ lang.labels.mortgage_advantage }}<br>
                            {{ formatted_mortage_advantage }} {{ lang.labels.currency }}<br>
                            ({{ advantage_percent }} %)
                        </div>

                        <donut
                            :percent="100 - advantage_percent"
                            background-color="#50bf77"
                            foreground-color="#148240"
                            :stroke-width="25"
                            :radius="60"
                        />

                        <div class="chart-lower-legend">
                            {{ lang.labels.mortgage }}<br>
                            {{ local_mortgage }} {{ lang.labels.currency }}<br>
                            ({{( 100 - advantage_percent) }} %)
                        </div>
                    </div>

                    <div
                        v-if="options.payment_type && options.payment_type.length > 1"
                        class="results"
                    >
                        <div class="payment">
                            <div class="elements">
                                <input
                                    v-model="payment_type"
                                    id="input-payment-type-fixed"
                                    name="payment-type"
                                    class="form-check-input"
                                    value="TF"
                                    type="radio"
                                >

                                <label
                                    class="form-check-label"
                                    for="input-payment-type-fixed"
                                >
                                    {{ lang.labels.fixed_payment }}:
                                </label>
                            </div>
                            <div class="description">
                                {{ fixed_payment }} {{ lang.labels.currency }}
                            </div>
                        </div>

                        <div class="payment">
                            <div class="elements">
                                <input
                                    v-model="payment_type"
                                    id="input-payment-type-variable"
                                    name="payment-type"
                                    class="form-check-input"
                                    value="TV"
                                    type="radio"
                                >
                                <label
                                    class="form-check-label"
                                    for="input-payment-type-variable"
                                >
                                    {{ lang.labels.variable_payment }}:
                                </label>
                            </div>
                            <div class="description">
                                {{ variable_payment }} {{ lang.labels.currency }}
                            </div>
                        </div>
                    </div>

                    <div v-else>
                        <strong>{{ lang.labels.mortgage_result }}</strong>: {{ fixed_payment }} {{ lang.labels.currency }}
                    </div>

                    <div
                        v-if="options.external"
                        class="actions"
                    >
                        <a :href="mortgageLink" class="btn btn-network" target="_blank">{{ lang.labels.mortgage_advice }}</a>
                    </div>

                    <div class="text-center m-2"></div>
                </div>
            </template>
        </modal>
</template>

<script>
import Donut from '../Donut'
import Modal from '../Modal'

import { numberFormat, getNumber } from '../../helpers/numbers'

export default {
    components: {
        'donut': Donut,
        'modal': Modal,
    },

    props: {
        estateId: {
            type: [String, Number],
            required: false,
        },

        price: {
            type: Number,
            required: true,
        },

        // mortgage: {
        //     type: Number,
        // },

        durations: {
            type: Object,
            default: () => {}
        },

        options: {
            type: Object,
            default: () => {
                return {
                    financial_agency: null,
                    financial_agent: null,
                    url: null,
                    payment_type: null,
                    user: null,
                    duration: null,
                }
            }
        },
    },

    data() {
        return {
            local_mortgage: this.numberFormat((this.mortgage || this.price * (!this.options.user ? 0.8 : this.options.user[Object.keys(this.options.user)[0]])), 0, ',', '.'),
            local_duration: this.options.duration,
            duration_object: null,
            payment_type_default: 'TF',
            payment_type: null,
            user: this.options.user ? this.options.user[Object.keys(this.options.user)[0]] : null
        }
    },

    computed: {
        formattedDurations() {
            let formatted = []

            for (let duration in this.durations) {
                if (duration != 0) {
                    formatted.push({
                        id: duration,
                        name: duration + ' ' + this.trans('labels.' + this.options.period)
                    })
                }
            }

            return formatted
        },

        formattedUser() {
            let formatted = []

            for (let [text, type] of Object.entries(this.options.user)) {
                formatted.push({
                    id: type,
                    name: this.lang.mortgage.user[text]
                })
            }

            return formatted
        },

        lang() {
            return this.$root.lang.estate_detail
        },

        numeric_mortgage() {
            return getNumber(this.local_mortgage)
        },

        formatted_price() {
            return this.numberFormat(this.price)
        },

        mortage_advantage() {
            return this.price - this.numeric_mortgage
        },

        formatted_mortage_advantage() {
            return this.numberFormat(this.mortage_advantage);
        },

        advantage_percent() {
            return Math.ceil((this.mortage_advantage * 100) / this.price)
        },

        fixed_payment() {
            return this.numberFormat(
                this.calculatePayment(
                    this.numeric_mortgage,
                    this.local_duration,
                    this.durations[!this.options.fixed_rate ? this.local_duration : 0].TASSO__FIN
                )
            )
        },

        variable_payment() {
            return this.numberFormat(
                this.calculatePayment(
                    this.numeric_mortgage,
                    this.local_duration,
                    this.durations[this.local_duration].TASSO_VAR
                )
            )
        },

        mortgageLink() {
           return this.options.url
                    + `agenzia=${this.options.financial_agency}`
                    + `&agenteFinanziario=${this.options.financial_agent}`
                    + `&idImmobile=${this.estateId}`
                    + `&importo_immobile=${this.price}`
                    + `&importo_mutuo=${this.numeric_mortgage}`
                    + `&durata_mutuo=${this.local_duration}`
                    + `&tipo_tasso=${this.payment_type}`
                    + `&sito_provenienza=${window.location.href}`
        }
    },

    mounted() {
        this.duration_object = this.formattedDurations.find(duration => {
            return duration.id == this.options.duration
        })
        this.payment_type = this.options.payment_type_default ||  this.payment_type_default
    },

    methods: {
        numberFormat(number) {
            return numberFormat(number, 0 , ',', '.')
        },

        changeMortgage(event) {
            let new_mortgage = getNumber(event)

            new_mortgage = new_mortgage < this.price
                ? new_mortgage
                : this.price

            if (new_mortgage == this.local_mortgage) {
                this.local_mortgage = event
            }

            this.$nextTick(() => {
                this.local_mortgage = this.numberFormat(new_mortgage)
            })
        },

        changeUser(event) {
            this.local_mortgage = this.numberFormat(this.price * event)
        },

        calculatePayment(mortgage, years, rate) {
            let payment
            if (this.user) {
                mortgage = mortgage * this.user
            }

            if (this.options.period == 'y') {
                let decimal_monthly_rate = (rate / 12) / 100
                payment = (mortgage * decimal_monthly_rate) / (
                    1 - (
                        1 / (
                            Math.pow(1 + decimal_monthly_rate, years * 12)
                        )
                    )
                )
            } else {
                let decimal_yearly_rate = rate / 100

                payment = (mortgage * decimal_yearly_rate) / (
                    1 - (
                        1 / (
                            Math.pow(1 + decimal_yearly_rate, years)
                        )
                    )
                )
            }

            return payment
        }
    }
}
</script>
